/**
 * Color Rules panel for the Photo List Settings page: the rules that color the
 * Daily Photo List's cells on the Excel, this website and the iPhone app alike
 * (backend services/photo_list_highlighting.py; plan:
 * photo-list-rules-and-progress.md, Part B). Each rule reads as a sentence.
 * Rules run top to bottom and a lower rule's color wins, so the drag order is
 * the precedence. A group with no rules of its own is colored by the generic
 * starter rules, shown read-only until an operator chooses to edit them.
 */
import React, { useState } from "react"
import { Pencil, Trash2 } from "lucide-react"

import {
  PhotoListHighlightOptions,
  PhotoListHighlightRule,
  PhotoListHighlightRuleInput,
  PhotoListHighlightRulesPayload,
  PhotoListHighlightSource,
  PhotoListRooftopOption,
  createHighlightRule,
  deleteHighlightRule,
  loadStarterHighlightRules,
  reorderHighlightRules,
  updateHighlightRule,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"
import { FILL_BY_COLOR } from "../../utils/photoListStyling"
import DragReorderList, { DragHandle, DragHandleProps } from "./DragReorderList"

interface Props {
  rules: PhotoListHighlightRule[]
  source: PhotoListHighlightSource
  options: PhotoListHighlightOptions
  rooftopOptions: PhotoListRooftopOption[]
  dealerGroupId: number
  /** Every write returns the group's whole rule list. */
  onRulesChanged: (payload: PhotoListHighlightRulesPayload) => void
  onError: (message: string | null) => void
}

const NEW_RULE: PhotoListHighlightRuleInput = {
  enabled: true,
  field: "sale_status_raw",
  condition: "is_one_of",
  values: [],
  recon_color: null,
  lot_requirement: null,
  columns: ["vin", "stock"],
  color: "green",
  vehicle_type: null,
  rooftops: [],
  bold_value: false,
}

const RECON_FIELD = "recon_status_raw"

export default function HighlightRulesPanel({
  rules,
  source,
  options,
  rooftopOptions,
  dealerGroupId,
  onRulesChanged,
  onError,
}: Props) {
  const isMobile = useIsMobile()
  const editable = source === "database"
  // Which rule's editor is open: a rule id, "new", or none.
  const [editing, setEditing] = useState<number | "new" | null>(null)
  const [busy, setBusy] = useState(false)
  // Bumped to start the drag list over from the rules INVI holds.
  const [listVersion, setListVersion] = useState(0)

  const run = async (write: () => Promise<PhotoListHighlightRulesPayload>, failure: string) => {
    setBusy(true)
    onError(null)
    try {
      onRulesChanged(await write())
      return true
    } catch (err) {
      onError(err instanceof Error ? err.message : failure)
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleSave = async (rule: PhotoListHighlightRule | null, draft: PhotoListHighlightRuleInput) => {
    const saved = await run(
      () =>
        rule?.id != null
          ? updateHighlightRule(rule.id, draft, dealerGroupId)
          : createHighlightRule(draft, dealerGroupId),
      "Failed to save the rule",
    )
    if (saved) setEditing(null)
  }

  const handleDelete = (rule: PhotoListHighlightRule) => {
    if (rule.id == null) return
    const ruleId = rule.id
    if (!window.confirm(`Delete this rule? ${ruleSentence(rule, options, rooftopOptions)}`)) return
    void run(() => deleteHighlightRule(ruleId, dealerGroupId), "Failed to delete the rule")
  }

  const handleReorder = (orderedKeys: Array<number | string>) => {
    void run(
      () => reorderHighlightRules(orderedKeys as number[], dealerGroupId),
      "Failed to save the new order",
    ).then((saved) => {
      // A failed save left the dragged order on screen while INVI kept the
      // old one, and the list only resyncs when the rules change — so the
      // wrong precedence stayed until a reload (review, 2026-09-25).
      if (!saved) setListVersion((version) => version + 1)
    })
  }

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>Color Rules</div>
      <p style={panelSubtitleStyle}>
        These color the list's cells on the Excel, this website and the iPhone
        app. Rules run top to bottom, and where two paint the same cell the
        lower one wins — drag to change which. Recon step colors come from
        Recon Steps above, and lots from the walking orders and front-lot
        boosts.
      </p>

      {!editable && (
        <div style={starterBannerStyle}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>Showing INVI's starter rules</div>
            <div style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
              This group has no color rules of its own yet. Edit to make these its own.
            </div>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              // Only while the group still has no rules: this page may be
              // older than rules another operator saved since.
              void run(
                () => loadStarterHighlightRules(dealerGroupId, { onlyIfEmpty: true }),
                "Failed to copy the rules",
              )
            }
            style={primaryButtonStyle(busy)}
          >
            Edit these rules
          </button>
        </div>
      )}

      <DragReorderList
        key={listVersion}
        items={rules}
        getKey={(rule) => rule.id ?? `starter-${rule.position}`}
        onReorder={handleReorder}
        disabled={!editable || busy || editing !== null}
        renderItem={(rule, handleProps) =>
          editing !== null && editing === rule.id ? (
            <RuleEditor
              initial={rule}
              options={options}
              rooftopOptions={rooftopOptions}
              busy={busy}
              onSave={(draft) => void handleSave(rule, draft)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <RuleRow
              rule={rule}
              options={options}
              rooftopOptions={rooftopOptions}
              editable={editable && !busy}
              isMobile={isMobile}
              handleProps={handleProps}
              onToggle={(enabled) =>
                rule.id != null &&
                void run(
                  () => updateHighlightRule(rule.id as number, { enabled }, dealerGroupId),
                  "Failed to switch the rule",
                )
              }
              onEdit={() => setEditing(rule.id)}
              onDelete={() => handleDelete(rule)}
            />
          )
        }
      />

      {editable && editing === "new" && (
        <RuleEditor
          initial={NEW_RULE}
          options={options}
          rooftopOptions={rooftopOptions}
          busy={busy}
          onSave={(draft) => void handleSave(null, draft)}
          onCancel={() => setEditing(null)}
        />
      )}
      {editable && editing === null && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          style={{ ...primaryButtonStyle(false), marginTop: THEME.spacing.md }}
        >
          Add Rule
        </button>
      )}
    </section>
  )
}

function RuleRow({
  rule,
  options,
  rooftopOptions,
  editable,
  isMobile,
  handleProps,
  onToggle,
  onEdit,
  onDelete,
}: {
  rule: PhotoListHighlightRule
  options: PhotoListHighlightOptions
  rooftopOptions: PhotoListRooftopOption[]
  editable: boolean
  isMobile: boolean
  handleProps: DragHandleProps
  onToggle: (enabled: boolean) => void
  onEdit: () => void
  onDelete: () => void
}) {
  const sentence = (
    <span style={{ ...sentenceStyle, opacity: rule.enabled ? 1 : 0.5 }}>
      <span style={swatchStyle(FILL_BY_COLOR[rule.color])} aria-hidden />
      {ruleSentence(rule, options, rooftopOptions)}
    </span>
  )
  const controls = editable && (
    <span style={{ display: "inline-flex", gap: THEME.spacing.xs }}>
      <button type="button" onClick={onEdit} aria-label="Edit rule" style={iconButtonStyle}>
        <Pencil size={14} strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete rule"
        style={{ ...iconButtonStyle, color: THEME.colors.semantic.danger }}
      >
        <Trash2 size={15} strokeWidth={2} />
      </button>
    </span>
  )
  const toggle = (
    <input
      type="checkbox"
      checked={rule.enabled}
      disabled={!editable}
      onChange={(e) => onToggle(e.target.checked)}
      aria-label={rule.enabled ? "Switch rule off" : "Switch rule on"}
      title={rule.enabled ? "On" : "Off"}
    />
  )
  const handle = editable ? <DragHandle {...handleProps} /> : <span />

  if (isMobile) {
    return (
      <div style={mobileRowStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: THEME.spacing.xs }}>
          {handle}
          {sentence}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label style={checkboxLabelStyle}>{toggle} On</label>
          {controls}
        </div>
      </div>
    )
  }
  return (
    <div style={rowStyle}>
      {handle}
      {toggle}
      {sentence}
      {controls || <span />}
    </div>
  )
}

/** "When Status is one of S, on one of its store's NEW walking-order lots →
 * paint Status, STOP, VIN, Stock, Stargard green · NEW only · every store". */
export function ruleSentence(
  rule: Pick<
    PhotoListHighlightRule,
    | "field" | "condition" | "values" | "recon_color" | "lot_requirement"
    | "columns" | "color" | "vehicle_type" | "rooftops" | "bold_value"
  >,
  options: PhotoListHighlightOptions,
  rooftopOptions: PhotoListRooftopOption[],
): string {
  const label = (field: string) => options.fields.find((f) => f.key === field)?.label ?? field
  let check: string
  if (rule.condition === "is_not_blank") check = "is not blank"
  else if (rule.recon_color) {
    const article = /^[aeiou]/i.test(rule.recon_color) ? "an" : "a"
    check = `is ${article} ${rule.recon_color} recon step`
  }
  else check = `is one of ${rule.values.join(", ")}`
  const lot = options.lot_requirements.find((l) => l.key === rule.lot_requirement)
  const stores = rule.rooftops.length
    ? rule.rooftops.map((name) => rooftopOptions.find((r) => r.name === name)?.label ?? name).join(", ")
    : "every store"
  const kind = rule.vehicle_type ? `${rule.vehicle_type} only` : "NEW and USED"
  const parts = [
    `When ${label(rule.field)} ${check}${lot ? `, ${lot.label}` : ""}`,
    `→ paint ${rule.columns.map(label).join(", ")} ${rule.color}`,
    kind,
    stores,
  ]
  if (rule.bold_value) parts.push(`bold ${label(rule.field)} text`)
  return parts.join(" · ").replace(" · →", " →")
}

function RuleEditor({
  initial,
  options,
  rooftopOptions,
  busy,
  onSave,
  onCancel,
}: {
  initial: PhotoListHighlightRuleInput
  options: PhotoListHighlightOptions
  rooftopOptions: PhotoListRooftopOption[]
  busy: boolean
  onSave: (draft: PhotoListHighlightRuleInput) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<PhotoListHighlightRuleInput>({
    enabled: initial.enabled,
    field: initial.field,
    condition: initial.condition,
    values: initial.values,
    recon_color: initial.recon_color,
    lot_requirement: initial.lot_requirement,
    columns: initial.columns,
    color: initial.color,
    vehicle_type: initial.vehicle_type,
    rooftops: initial.rooftops,
    bold_value: initial.bold_value,
  })
  const [valuesText, setValuesText] = useState(initial.values.join(", "))
  const set = (updates: Partial<PhotoListHighlightRuleInput>) => setDraft((d) => ({ ...d, ...updates }))
  const toggleIn = (list: string[], item: string) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
  const usesReconColors = draft.condition === "is_one_of" && draft.recon_color !== null
  // Stores the rule names that the page's options don't list still show.
  const storeChoices = [
    ...rooftopOptions,
    ...draft.rooftops
      .filter((name) => !rooftopOptions.some((r) => r.name === name))
      .map((name) => ({ name, label: name })),
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const values = usesReconColors || draft.condition === "is_not_blank"
      ? []
      : valuesText.split(",").map((v) => v.trim()).filter(Boolean)
    onSave({ ...draft, values })
  }

  return (
    <form onSubmit={handleSubmit} style={editorStyle}>
      <div style={editorLineStyle}>
        <span style={editorLabelStyle}>When</span>
        <select
          value={draft.field}
          onChange={(e) =>
            set({
              field: e.target.value,
              recon_color: e.target.value === RECON_FIELD ? draft.recon_color : null,
            })
          }
          style={selectStyle}
          aria-label="Column the rule reads"
        >
          {options.fields.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        <select
          value={draft.condition}
          onChange={(e) => set({ condition: e.target.value as PhotoListHighlightRuleInput["condition"] })}
          style={selectStyle}
          aria-label="What the rule checks"
        >
          {options.conditions.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        {draft.condition === "is_one_of" && !usesReconColors && (
          <input
            type="text"
            value={valuesText}
            onChange={(e) => setValuesText(e.target.value)}
            placeholder="Values, separated by commas"
            style={{ ...inputStyle, flex: 1, minWidth: 160 }}
            aria-label="Values to match"
          />
        )}
        {draft.condition === "is_one_of" && draft.field === RECON_FIELD && (
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={usesReconColors}
              onChange={(e) => set({ recon_color: e.target.checked ? options.recon_colors[0] : null })}
            />
            the recon steps colored
            <select
              value={draft.recon_color ?? ""}
              disabled={!usesReconColors}
              onChange={(e) => set({ recon_color: e.target.value as PhotoListHighlightRule["recon_color"] })}
              style={selectStyle}
              aria-label="Recon step color"
            >
              {options.recon_colors.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      {/* A rule reading a column a sheet doesn't show never fires there, and
          saving gave no hint of it (review, 2026-09-25). */}
      <div style={hintStyle}>Only applies on sheets that show this column.</div>

      <div style={editorLineStyle}>
        <span style={editorLabelStyle}>And</span>
        <select
          value={draft.lot_requirement ?? ""}
          onChange={(e) =>
            set({ lot_requirement: (e.target.value || null) as PhotoListHighlightRule["lot_requirement"] })
          }
          style={selectStyle}
          aria-label="Lot the car must be on"
        >
          <option value="">parked anywhere</option>
          {options.lot_requirements.map((l) => (
            <option key={l.key} value={l.key}>{l.label}</option>
          ))}
        </select>
      </div>

      <div style={editorLineStyle}>
        <span style={editorLabelStyle}>Paint</span>
        {options.fields.map((f) => (
          <label key={f.key} style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={draft.columns.includes(f.key)}
              onChange={() => set({ columns: toggleIn(draft.columns, f.key) })}
            />
            {f.label}
          </label>
        ))}
      </div>

      <div style={editorLineStyle}>
        <span style={editorLabelStyle}>Color</span>
        <span style={swatchStyle(FILL_BY_COLOR[draft.color])} aria-hidden />
        <select
          value={draft.color}
          onChange={(e) => set({ color: e.target.value as PhotoListHighlightRule["color"] })}
          style={selectStyle}
          aria-label="Color"
        >
          {options.colors.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span style={editorLabelStyle}>For</span>
        <select
          value={draft.vehicle_type ?? ""}
          onChange={(e) =>
            set({ vehicle_type: (e.target.value || null) as PhotoListHighlightRule["vehicle_type"] })
          }
          style={selectStyle}
          aria-label="NEW or USED"
        >
          <option value="">NEW and USED</option>
          {options.vehicle_types.map((t) => (
            <option key={t} value={t}>{t} only</option>
          ))}
        </select>
      </div>

      <div style={editorLineStyle}>
        <span style={editorLabelStyle}>At</span>
        {storeChoices.map((r) => (
          <label key={r.name} style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={draft.rooftops.includes(r.name)}
              onChange={() => set({ rooftops: toggleIn(draft.rooftops, r.name) })}
            />
            {r.label}
          </label>
        ))}
        <span style={hintStyle}>(none ticked = every store)</span>
      </div>

      <div style={editorLineStyle}>
        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={draft.bold_value}
            onChange={(e) => set({ bold_value: e.target.checked })}
          />
          Bold the {options.fields.find((f) => f.key === draft.field)?.label ?? draft.field} text
        </label>
      </div>

      <div style={{ display: "flex", gap: THEME.spacing.sm }}>
        <button type="submit" disabled={busy} style={primaryButtonStyle(busy)}>
          {busy ? "Saving…" : "Save rule"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  )
}

// --- Local styles (panel chrome shared with ReconStepsPanel) ---

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 4,
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

const starterBannerStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: THEME.spacing.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.white,
}

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px 24px minmax(0, 1fr) auto",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const mobileRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.sm} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const sentenceStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  gap: 8,
  fontSize: 13,
  lineHeight: 1.4,
  color: THEME.colors.neutral.light.textPrimary,
  minWidth: 0,
}

const swatchStyle = (color: string | undefined): React.CSSProperties => ({
  display: "inline-block",
  width: 12,
  height: 12,
  borderRadius: 3,
  border: `1px solid ${THEME.colors.silver}`,
  background: color ?? "transparent",
  flexShrink: 0,
})

const iconButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
}

const checkboxLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

const editorStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.md,
  margin: `${THEME.spacing.xs} 0`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.white,
}

const editorLineStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: THEME.spacing.sm,
}

const editorLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  minWidth: 40,
  color: THEME.colors.neutral.light.textSecondary,
}

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
}

const inputStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  maxWidth: "100%",
}

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  alignSelf: "flex-start",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  background: "transparent",
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
